import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /**
   * Numero di pagine caricate automaticamente prima di mostrare il bottone
   * "Carica altri". Mantiene il DOM contenuto e lo scroll fluido anche su
 * collezioni lunghe (pattern diffuso nei grandi cataloghi moda).
   */
  static AUTO_LOAD_LIMIT = 2;

  /** Conteggio pagine auto-caricate nel "ciclo" corrente. */
  #autoLoadCount = 0;

  /** vcb: documenti gia' analizzati per pagina, per non rifare il DOMParser
   *  (~800 KB) una seconda volta per la barra di paginazione. */
  #docs = new Map();

 /** Limite auto-load per-istanza via attributo data-auto-load-limit. 0 = solo bottone. */
  get autoLoadLimit() {
    const a = this.getAttribute('data-auto-load-limit');
    return a != null && a !== '' ? Number(a) : PaginatedList.AUTO_LOAD_LIMIT;
  }

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Macchina "infinite scroll / Carica altri": prefetch pagine adiacenti,
    // observer del sentinel e spinner. NON deve girare con la paginazione
    // classica (infinite-scroll="false"): era codice residuo che pre-scaricava
    // pagine pesanti a ogni load, rallentando lo scroll e il fondo pagina.
    if (this.getAttribute('infinite-scroll') !== 'false') {
      // In modalita solo-bottone (autoLoadLimit 0) NON pre-scarichiamo le pagine
      // adiacenti sul load: era un fetch pesante (N prodotti) in sottofondo nella
      // finestra critica. La pagina successiva si carica on-demand al click.
      if (this.autoLoadLimit !== 0) {
        this.#fetchPage('next');
        this.#fetchPage('previous');
      }
      this.#observeViewMore();
      this.#updateInfiniteScrollState();
 // Modalita solo-bottone: nessun auto-load, mostra subito "Carica altri".
      if (this.autoLoadLimit === 0) {
        this.#enterManualMode();
        this.#prefetchNextWhenButtonNears();
      }
    }

    // Listener delegato per il bottone "Carica altri" (event delegation
    // così funziona anche se il bottone viene re-renderizzato).
    this.addEventListener('click', this.#handleHostClick);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.removeEventListener('click', this.#handleHostClick);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Click delegato sul host. Intercetta il bottone "Carica altri prodotti".
   */
  #handleHostClick = (event) => {
    const target = event.target;
    if (target && target.classList && target.classList.contains('infinite-scroll-load-more')) {
      event.preventDefault();
      this.#handleLoadMoreClick();
    }
  };

  /**
   * Click sul "Carica altri": esce dalla modalità manuale (resetta il contatore
   * e ri-osserva il sentinel) e renderizza la pagina successiva.
   */
  async #handleLoadMoreClick() {
    // vcb: feedback immediato al clic (08/09/2026). Senza un cambiamento a
    // video il browser aspetta la fine dell'append di 72 schede per dipingere,
    // e l'INP del clic era scarso il 45% delle volte. Con [loading] lo spinner
    // compare subito e l'interazione si chiude li'.
    this.setAttribute('loading', '');
    this.setAttribute('aria-busy', 'true');
    try {
      // Modalita solo-bottone: carica una pagina e resta in manuale.
      if (this.autoLoadLimit === 0) {
        await this.#renderNextPage();
        this.#enterManualMode();
        return;
      }
      this.#exitManualMode();
      await this.#renderNextPage();
    } finally {
      this.removeAttribute('loading');
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Entra in modalità manuale: smette di osservare il sentinel e mostra
   * il bottone "Carica altri" via attributo CSS.
   */
  #enterManualMode() {
    this.setAttribute('manual-load-more', '');
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }
  }

  /**
   * Esce dalla modalità manuale: resetta il contatore e ri-osserva il sentinel.
   */
  #exitManualMode() {
    this.removeAttribute('manual-load-more');
    this.#autoLoadCount = 0;
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Wrapper chiamato dall'observer al posto di renderNextPage diretto.
   * Carica automaticamente fino ad AUTO_LOAD_LIMIT pagine, poi entra in
   * modalità manuale (mostra il bottone "Carica altri").
   */
  async #handleNextPageObserved() {
    if (this.#autoLoadCount >= this.autoLoadLimit) {
      this.#enterManualMode();
      return;
    }
    this.#autoLoadCount++;
    await this.#renderNextPage();
  }

  /**
   * Mostra/nasconde lo spinner di caricamento in base alla presenza di una pagina
   * successiva. Quando non ci sono altre pagine impostiamo l'attributo
   * `infinite-complete`, così la CSS nasconde lo spinner ed evita che l'utente
   * (specie su connessioni lente) confonda l'attesa con la fine della collezione.
   */
  #updateInfiniteScrollState() {
    const { grid } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage);
    if (!lastPage) {
      this.toggleAttribute('infinite-complete', true);
      return;
    }

    // Leggiamo direttamente dal DOM invece di usare this.refs.cards che
    // potrebbe non essere ancora aggiornato dal MutationObserver dopo
    // un grid.append(). Senza questo, lo spinner resta visibile anche
    // quando la collezione è terminata.
    const allCards = grid.querySelectorAll(':scope > [data-page]');
    const lastCard = allCards[allCards.length - 1];

    if (!lastCard) {
      this.toggleAttribute('infinite-complete', true);
      return;
    }

    const currentPage = Number(lastCard.dataset.page);
    const hasNextPage = currentPage < lastPage;
    this.toggleAttribute('infinite-complete', !hasNextPage);

    // Se la collezione è terminata, smetti di osservare il sentinel
    // per evitare callback inutili dell'IntersectionObserver.
    if (!hasNextPage) {
      const { viewMoreNext } = this.refs;
      if (viewMoreNext && this.infinityScrollObserver) {
        this.infinityScrollObserver.unobserve(viewMoreNext);
      }
    }
  }

  /**
   * Modalita solo-bottone: scalda la pagina successiva quando il thread
   * principale e' libero, senza renderizzarla.
   *
   * Il prefetch al load era stato tolto perche' pesava sulla finestra critica:
   * era una `#fetchPage('next')` SINCRONA dentro `connectedCallback`, in gara
   * con immagini e script della prima pittura. Qui la richiesta viene messa in
   * coda con `requestIdleCallback`, quindi parte solo a thread libero — e
   * toglie l'attesa dal click, misurata 2.709 ms il 20/08/2026 di cui 814 ms
   * di sola risposta del server, con ZERO long task (era attesa, non lavoro).
   *
   * ⚠️ Due innescchi piu' "intelligenti" sono stati provati e scartati il
   * 20/08/2026, entrambi perche' non partivano mai:
   *   - IntersectionObserver sul sentinel: al momento di `connectedCallback` il
   *     ref `viewMoreNext` non e' ancora risolto, quindi non veniva armato.
   *   - listener di `scroll` con `once`: non verificabile e piu' fragile.
   * L'idle callback non dipende ne' dai ref ne' dagli eventi.
   *
   * Costo per chi non clicca mai: una richiesta da ~40 KB a pagina dipinta.
   */
  #prefetchNextWhenButtonNears() {
    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  #observeViewMore() {
 // Modalita solo-bottone (autoLoadLimit 0): nessun auto-load su scroll,
    // ne avanti ne indietro. Evita che atterrando su ?page=2 il sentinel
    // "previous" in cima pre-carichi la pagina precedente (bug: partiva dal
    // primo prodotto della pagina prima). Il bottone gestisce l'avanzamento.
    if (this.autoLoadLimit === 0) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#handleNextPageObserved();
              }
            }
          }
        },
        {
          // Pre-renderizza la pagina successiva con ampio anticipo (~1.5 schermate)
          // così lo scroll continuo non si "blocca" al confine tra una pagina e l'altra.
          rootMargin: '800px 0px 1200px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    // vcb: la pagina e' quasi sempre gia' in cache (prefetch all'avvicinarsi
    // del bottone), quindi parsing e append partirebbero nello stesso task del
    // clic. Un frame di respiro lascia dipingere lo spinner prima.
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    this.#updateInfiniteScrollState();
    this.#updateNavBarToPage(nextPage.page);

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * VCB: al "Carica altri" aggiorna la nav-bar (testo "Pagina X di Y" + numero
   * attivo nella paginazione) alla pagina appena caricata, cosi' l'indicatore
   * avanza a ogni click e a fine collezione l'utente vede l'ultima pagina invece
   * di restare bloccato su "Pagina 1" (evita di credere che ci siano altre pagine).
   * @param {number} pageNum
   */
  #updateNavBarToPage(pageNum) {
    if (!pageNum) return;

    // Sorgente preferita: la barra di paginazione che Shopify ha GIA' renderizzato
    // per QUESTA pagina, dentro l'HTML di sezione che abbiamo scaricato per caricare
    // i prodotti (this.pages). Shopify centra la finestra sulla pagina corrente
    // ("1 … 4 [5] 6 … 46"), quindi la pagina attiva e' SEMPRE visibile ed evidenziata,
    // anche quando altrimenti cadrebbe dentro i "…". Sostituiamo la .pagination live
    // con quella fresca (i sentinel infinite-scroll sono fuori dalla .pagination:
    // lo swap non li stacca) e aggiorniamo il testo "Pagina X di Y".
    const freshHTML = this.pages.get(pageNum);
    if (freshHTML) {
      const doc = this.#docs.get(pageNum) || new DOMParser().parseFromString(freshHTML, 'text/html');
      const freshPagination = doc.querySelector('.pagination');
      const livePagination = this.querySelector('.pagination');
      if (freshPagination && livePagination) {
        livePagination.replaceWith(freshPagination);
        freshPagination.setAttribute('data-current_page', String(pageNum));
      }
      const freshInfo = doc.querySelector('.cnb-page');
      const liveInfo = this.querySelector('.cnb-page');
      if (freshInfo && liveInfo) liveInfo.textContent = freshInfo.textContent;
      // Se abbiamo trovato e sostituito la paginazione, abbiamo finito.
      if (freshPagination && livePagination) return;
    }

    // Fallback (HTML di sezione non disponibile): aggiorna almeno il testo e sposta
    // l'evidenziazione su un link gia' presente nel DOM, se c'e'.
    const info = this.querySelector('.cnb-page');
    if (info) {
      const m = info.textContent.match(/di\s+(\d+)/i);
      if (m) info.textContent = 'Pagina ' + pageNum + ' di ' + m[1];
    }
    const nav = this.querySelector('.pagination');
    if (!nav) return;
    nav.querySelectorAll('[aria-current="page"], .pagination__link--current').forEach((el) => {
      el.classList.remove('pagination__link--current');
      el.removeAttribute('aria-current');
    });
    nav.querySelectorAll('.pagination__link--page').forEach((el) => {
      if (el.textContent.trim() === String(pageNum)) {
        el.classList.add('pagination__link--current');
        el.setAttribute('aria-current', 'page');
      }
    });
    nav.setAttribute('data-current_page', String(pageNum));
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    let parsedPage = this.#docs.get(page);
    if (!parsedPage) {
      parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
      this.#docs.set(page, parsedPage);
    }
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#docs.clear();

    // Reset modalità manuale (un nuovo filtro = nuova esperienza, riparti da zero)
    this.#autoLoadCount = 0;
    this.removeAttribute('manual-load-more');

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // Bug fix spinner infinito dopo filtro: il morph del re-render rimuove
    // l'attributo `infinite-complete` dall'host, ma lo stato veniva ricalcolato
    // SOLO se cambiava data-last-page. Con lo stesso numero di pagine
    // (es. 1 -> 1 filtrando una collezione piccola) lo spinner restava visibile
    // per sempre. Ora lo stato viene ricalcolato a OGNI mutazione della griglia,
    // con un debounce via requestAnimationFrame per aspettare che il morph finisca.
    let refreshScheduled = false;
    const refreshState = () => {
      if (refreshScheduled) return;
      refreshScheduled = true;
      requestAnimationFrame(() => {
        refreshScheduled = false;
        if (!this.isConnected) return;
        this.#observeViewMore();
        this.#updateInfiniteScrollState();
      });
    };

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // La griglia è cambiata: ricalcola sempre lo stato dello spinner
      refreshState();

      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Fallback: dopo 8s smetti di osservare (copre anche connessioni lente)
      // e fai un ultimo ricalcolo dello stato per sicurezza.
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
        refreshState();
      }, 8000);
    }
  };
}

